import {IJournalDischargeDataRow} from './journal.controller';
import {ISnackbarService} from '../../snackbar/snackbar.service';
import {
    IDataValueApi,
    IDataValueEditRequest
} from '../../api/data-value-api.service';

const editDialogTemplate = require<string>('./edit-discharge-row-dialog.html');
const deleteDialogTemplate = require<string>(
    './delete-discharge-row-dialog.html');

export default class DischargeTableController {
  static $inject = ['$mdDialog', 'snackbarService', '$translate'];
  /**
   * Journal discharge data to display.
   */
  dischargeData:IJournalDischargeDataRow[];
  /**
   * Selected discharge data rows.
   */
  selectedDischargeRows:IJournalDischargeDataRow[];

  constructor(private $mdDialog:ng.material.IDialogService,
              private snackbarService:ISnackbarService,
              private $translate:ng.translate.ITranslateService) {
    this.selectedDischargeRows = [];
  }

  $onChanges() {
    this.selectedDischargeRows = [];
  }

  /**
   * Whether there is a selected discharge row.
   */
  get selectedDischargeRow():boolean {
    return this.selectedDischargeRows.length > 0;
  }

  /**
   * Prompt a dialog to edit the selected discharge data row.
   */
  editRow($event:MouseEvent) {
    const selectedDischargeRow = this.selectedDischargeRows[0];
    const dialogOptions:ng.material.IDialogOptions = {
      locals: {
        dischargeDataRow: selectedDischargeRow
      },
      controller: DischargeTableEditDialogController,
      controllerAs: '$ctrl',
      escapeToClose: false,
      targetEvent: $event,
      template: editDialogTemplate
    };
    this.$mdDialog.show(dialogOptions)
        .then((response:{
          waterLevel:number,
          discharge:number,
          freeRiverArea:number,
          dischargeDataRow:IJournalDischargeDataRow}) => {
          response.dischargeDataRow.waterLevel.dataValue = response.waterLevel;
          response.dischargeDataRow.discharge.dataValue = response.discharge;
          if (response.dischargeDataRow.freeRiverArea) {
            response.dischargeDataRow.freeRiverArea.dataValue =
                response.freeRiverArea;
          }
          this.snackbarService.show({
            content: this.$translate.instant(
                'DISCHARGE_DATA_WAS_EDITED')
          });
        })
        .catch((error:any) => {
          if (error) {
            this.snackbarService.show({
              content: this.$translate.instant(
                  'ERROR_UPDATING_DISCHARGE_DATA')
            });
          }
        });
  }

  /**
   * Delete the selected discharge data row from the backend.
   */
  deleteRow($event:MouseEvent) {
    const dischargeDataRow = this.selectedDischargeRows[0];
    const dialogOptions:ng.material.IDialogOptions = {
      controller: DischargeTableDeleteDialogController,
      controllerAs: '$ctrl',
      escapeToClose: false,
      locals: {
        dischargeDataRow
      },
      targetEvent: $event,
      template: deleteDialogTemplate
    };
    this.$mdDialog.show(dialogOptions)
        .then(() => {
          this.selectedDischargeRows = [];
          const rowIndex = this.dischargeData.indexOf(dischargeDataRow);
          this.dischargeData.splice(rowIndex, 1);
          this.snackbarService.show({
            content: this.$translate.instant(
                'DISCHARGE_DATA_WAS_DELETED')
          });
        })
        .catch((error) => {
          if (error) {
            this.snackbarService.show({
              content: this.$translate.instant(
                  'ERROR_DELETING_DISCHARGE_DATA')
            })
          }
        });
  }
}

class DischargeTableEditDialogController {
  static $inject = ['dischargeDataRow', 'dataValueApi', '$mdDialog'];

  /**
   * Editable water level value.
   */
  waterLevel:number;
  /**
   * Editable discharge value.
   */
  discharge:number;
  /**
   * Editable cross-section area value.
   */
  freeRiverArea:number;
  /**
   * Whether there is an asynchronous operation in progress.
   */
  loadingInProgress:boolean;
  /**
   * Controller for the form.
   */
  editForm:ng.IFormController;
  /**
   * Whether the free river area input is disabled.
   */
  disabledFreeRiverAreaInput:boolean;

  constructor(private dischargeDataRow:IJournalDischargeDataRow,
              private dataValueApi:IDataValueApi,
              private $mdDialog:ng.material.IDialogService) {
    this.waterLevel = dischargeDataRow.waterLevel.dataValue;
    this.discharge = dischargeDataRow.discharge.dataValue;
    this.disabledFreeRiverAreaInput = dischargeDataRow.freeRiverArea == null;
    if (dischargeDataRow.freeRiverArea) {
      this.freeRiverArea = dischargeDataRow.freeRiverArea.dataValue;
    }
    this.loadingInProgress = false;
  }

  /**
   * Whether the apply button is disabled.
   */
  get disabledApplyButton() {
    if (this.editForm) {
      const unchangedWaterLevel =
          this.waterLevel == this.dischargeDataRow.waterLevel.dataValue;
      const unchangedDischarge =
          this.discharge == this.dischargeDataRow.discharge.dataValue;
      const unchangedFreeRiverArea = this.dischargeDataRow.freeRiverArea != null
          ? this.freeRiverArea == this.dischargeDataRow.freeRiverArea.dataValue
          : true;
      return this.editForm.$invalid || this.loadingInProgress ||
          (unchangedWaterLevel && unchangedDischarge && unchangedFreeRiverArea);
    } else {
      return true;
    }
  }

  /**
   * Apply the changes in the backend.
   */
  applyChanges() {
    this.loadingInProgress = true;
    const waterLevelRequest:IDataValueEditRequest = {
      dataValue: this.waterLevel,
      id: this.dischargeDataRow.waterLevel.id
    };
    const dischargeRequest:IDataValueEditRequest = {
      dataValue: this.discharge,
      id: this.dischargeDataRow.discharge.id
    };
    let freeRiverAreaRequest:IDataValueEditRequest = null;
    if (this.dischargeDataRow.freeRiverArea) {
      freeRiverAreaRequest = {
        dataValue: this.freeRiverArea,
        id: this.dischargeDataRow.freeRiverArea.id
      };
    }
    this.dataValueApi.editDischargeData(
        this.dischargeDataRow.waterLevel.siteId,
        waterLevelRequest, dischargeRequest, freeRiverAreaRequest)
        .then(() => {
          this.$mdDialog.hide({
            dischargeDataRow: this.dischargeDataRow,
            waterLevel: this.waterLevel,
            discharge: this.discharge,
            freeRiverArea: this.freeRiverArea
          });
        })
        .catch((error) => {
          this.$mdDialog.cancel(error);
        })
        .finally(() => {
          this.loadingInProgress = false;
        });
  }

  /**
   * Cancel the dialog without making changes.
   */
  cancel() {
    this.$mdDialog.cancel();
  }
}

class DischargeTableDeleteDialogController {
  static $inject = ['dischargeDataRow', 'dataValueApi', '$mdDialog'];

  /**
   * Whether a deletion operation is in progress.
   */
  loadingInProgress:boolean;

  constructor(private dischargeDataRow:IJournalDischargeDataRow,
              private dataValueApi:IDataValueApi,
              private $mdDialog:ng.material.IDialogService) {
    this.loadingInProgress = false;
  }

  /**
   * Delete the discharge data from the database.
   */
  confirmDelete() {
    this.loadingInProgress = true;
    this.dataValueApi.deleteDischargeData(
        this.dischargeDataRow.waterLevel.siteId,
        this.dischargeDataRow.waterLevel.id,
        this.dischargeDataRow.discharge.id).then(() => {
      this.$mdDialog.hide();
    }).catch((error) => {
      this.$mdDialog.cancel(error);
    }).finally(() => {
      this.loadingInProgress = false;
    });
  }

  /**
   * Cancel the dialog without making changes.
   */
  cancel() {
    this.$mdDialog.cancel();
  }
}



/** WEBPACK FOOTER **
 ** ./app/operational/journal/discharge-table.controller.ts
 **/